import React, { useState, useEffect, useCallback, memo } from "react";
import { Card, Select, DatePicker, Button, Table, Tag, message } from "antd";
import moment from "moment";
import { getAllStates, getFacilitiesByState, getAuditFacilityFilter, getPresignedReportUrl } from "./laundryAuditApi";

const { Option } = Select;
const { RangePicker } = DatePicker;

// audit_status: 0 = audit completed, 1 = rectification completed, 2 = rectification
// notified — same convention as Dashboard. Response shape here is flatter than
// Cleaning Audit's Reports (no separate report name/type — this is audit
// history for one facility filtered by date range), confirmed by reading
// MajesticERP-LaundryAuditAPI/code/models/AuditReportsModel.js's SQL directly:
// { audit_end_datetime, audit_status, first_name, last_name,
//   rectification_end_time, report_path }.
const STATUS_META = {
  0: { label: "Audit completed", color: "green" },
  1: { label: "Rectification completed", color: "green" },
  2: { label: "Rectification notified", color: "orange" },
};

const Reports = () => {
  const [states, setStates] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [stateId, setStateId] = useState(null);
  const [facilityId, setFacilityId] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    (async () => {
      const { error, result } = await getAllStates();
      setStates(!error && Array.isArray(result?.result) ? result.result : []);
    })();
  }, []);

  const onStateChange = useCallback(async (value) => {
    setStateId(value);
    setFacilityId(null);
    setFacilities([]);
    if (!value) return;
    const { error, result } = await getFacilitiesByState(value);
    setFacilities(!error && Array.isArray(result?.result) ? result.result : []);
  }, []);

  const search = useCallback(async () => {
    if (!facilityId) {
      message.error("Please select a state and facility first.");
      return;
    }
    setLoading(true);
    setSearched(true);
    const body = { facility_id: facilityId };
    if (dateRange) {
      body.from_date = dateRange[0].format("YYYY-MM-DD");
      body.to_date = dateRange[1].format("YYYY-MM-DD");
    }
    const { error, result } = await getAuditFacilityFilter(body);
    setRows(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, [facilityId, dateRange]);

  const download = useCallback(async (reportPath) => {
    const { error, result } = await getPresignedReportUrl(reportPath);
    if (error || !result?.success) {
      message.error("Couldn't open that report.");
      return;
    }
    window.open(result.result, "_blank", "noopener,noreferrer");
  }, []);

  const columns = [
    {
      title: "Auditor",
      key: "auditor",
      render: (_, row) => `${row.first_name || ""} ${row.last_name || ""}`.trim() || "-",
    },
    {
      title: "Audit Date",
      dataIndex: "audit_end_datetime",
      key: "audit_end_datetime",
      render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Rectification End",
      dataIndex: "rectification_end_time",
      key: "rectification_end_time",
      render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Status",
      dataIndex: "audit_status",
      key: "audit_status",
      render: (status) => {
        const meta = STATUS_META[status] || { label: "Unknown", color: "default" };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: "Report",
      key: "report",
      render: (_, row) => (row.report_path ? <Button size="small" onClick={() => download(row.report_path)}>Download Report</Button> : "-"),
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>State</div>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select a state"
              style={{ width: 200 }}
              value={stateId}
              onChange={onStateChange}
            >
              {states.map((s) => <Option key={s.state_id} value={s.state_id}>{s.state_name}</Option>)}
            </Select>
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Facility Name</div>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select a facility"
              style={{ width: 220 }}
              value={facilityId}
              onChange={setFacilityId}
              disabled={!stateId}
              notFoundContent={stateId ? "No facilities for this state" : "Select a state first"}
            >
              {facilities.map((f) => <Option key={f.facility_id} value={f.facility_id}>{f.facility_name}</Option>)}
            </Select>
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Date Range</div>
            <RangePicker format="DD-MM-YYYY" value={dateRange} onChange={setDateRange} disabledDate={(d) => d && d > moment().endOf("day")} />
          </div>
          <Button type="primary" loading={loading} onClick={search}>Search</Button>
        </div>
      </Card>

      {searched && (
        <Card title="Audit Report History">
          <Table
            rowKey={(_, i) => i}
            columns={columns}
            dataSource={rows}
            loading={loading}
            pagination={{ pageSize: 20 }}
            locale={{ emptyText: "No reports found for these filters." }}
          />
        </Card>
      )}
    </div>
  );
};

export default memo(Reports);
